import { useRef, useState } from 'react';
import { useDialog, useOverlay, useModal } from 'react-aria';
import { FocusScope } from 'react-aria';
import { CustomButton } from '@shared/components/CustomButton/CustomButton';
import { CustomTextInput } from '@shared/components/CustomTextInput/CustomTextInput';
import styles from './RequestMissingEpdModal.module.css';

export interface RequestMissingEpdModalTranslations {
  title: string,
  subtitle: string,
  placeholder: string,
  submitButton: string,
  aria: {
    closeModal: string,
  },
};

export interface RequestMissingEpdModalProps {
  onClose: () => void;
  onSubmit: (epdNameOrId: string) => void;
  translations: RequestMissingEpdModalTranslations;
}

export function RequestMissingEpdModal({
  onClose,
  onSubmit,
  translations
}: RequestMissingEpdModalProps) {
  const ref = useRef<HTMLDivElement>(null);

  const { overlayProps, underlayProps } = useOverlay(
    { isOpen: true, onClose, isDismissable: true },
    ref
  );
  const { modalProps } = useModal();
  const { dialogProps, titleProps } = useDialog({}, ref);

  const [epdNameOrId, setEpdNameOrId] = useState<string>('');

  const handleSubmit = () => {
    if (epdNameOrId.trim()) {
      onSubmit(epdNameOrId.trim());
      onClose();
    }
  };

  return (
    <div className={styles.overlay} {...underlayProps}>
      <FocusScope contain restoreFocus autoFocus>
        <div
          {...overlayProps}
          {...dialogProps}
          {...modalProps}
          ref={ref}
          className={styles.modal}
        >
          <header className={styles.header}>
            <div>
              <h2 {...titleProps} className={styles.title}>
                {translations.title}
              </h2>
              <p className={styles.subtitle}>
                {translations.subtitle}
              </p>
            </div>
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label={translations.aria.closeModal}
            >
              <span className="material-icons">close</span>
            </button>
          </header>

          <div className={styles.body}>
            <div className={styles.inputGroup}>
              <CustomTextInput
                placeholder={translations.placeholder}
                value={epdNameOrId}
                onChange={(value) => setEpdNameOrId(value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSubmit();
                  }
                }}
                fullWidth
                autoFocus
                hasNoRadius
                hasNoBoxShadow
                borderColor="#bcd0d1"
              />
            </div>
          </div>

          <footer className={styles.footer}>
            <CustomButton
              fullWidth={true}
              variant="primary"
              onPress={handleSubmit}
              size="small"
              isDisabled={!epdNameOrId.trim()}
              style={{ backgroundColor: '#1a5765' }}
            >
              {translations.submitButton}
            </CustomButton>
          </footer>
        </div>
      </FocusScope>
    </div>
  );
}
